import { ActionContext, Module } from "vuex";
import { LoggerService as lgg } from "vue-components-library";
import { ICaseTransfer } from "../models/ICaseTransfer";
import { TwiageCase } from "@/Case/models/TwiageCase";
import CaseTransferService from "@/Case/services/CaseTransfer.Service";
import CaseService from "../services/Case.Service";
import router from "@/App/router";
import { Storage as storage } from "@/App/services/LocalStorage";

type CaseTransferState = {
  caseTransfer: ICaseTransfer;
  caseTransferType: string | null;
  caseTransferCase: TwiageCase | null;
};

const DEFAULT_CASE_TRANSFER: ICaseTransfer = {
  _id: null,
  caseId: null,
  caseTransferCode: null,
  status: "",
  pubnubChannel: null,
  request: null,
  createdBy: null,
  messageForSender: null,
  messageForReceiver: null,
  emsAgency: null,
  updatedAt: null,
};

const CASE_TRANSFER_TYPE_TRANSFER = "transfer";
const CASE_TRANSFER_TYPE_REQUEST = "request";

const LOG_TAG = "[CASE TRANSFER STORE]";

function getDefaultCaseTransfer() {
  return JSON.parse(JSON.stringify(DEFAULT_CASE_TRANSFER));
}

function getDefaultState() {
  return {
    caseTransfer: storage.getItem("caseTransfer")
      ? JSON.parse(storage.getItem("caseTransfer") || "")
      : getDefaultCaseTransfer(),
    caseTransferType: storage.getItem("caseTransferType") ? storage.getItem("caseTransferType") : null,
    caseTransferCase: storage.getItem("caseTransferCase")
      ? JSON.parse(storage.getItem("caseTransferCase") || "")
      : null,
  };
}

const state = getDefaultState();

const getters = {
  caseTransfer: (state: CaseTransferState): ICaseTransfer => state.caseTransfer,
  caseTransferType: (state: CaseTransferState): string | null => state.caseTransferType,
  caseTransferCase: (state: CaseTransferState): TwiageCase | null => state.caseTransferCase,
};

const actions = {
  async open(context: ActionContext<CaseTransferState, unknown>, caseId: string) {
    const lggData = { case: { id: caseId } };

    try {
      const data = await CaseTransferService.open({
        caseId,
      });

      lgg.info(`${LOG_TAG} Case transfer initiated`, lggData);

      context.commit("SET_CASE_TRANSFER", data);
      context.commit("SET_CASE_TRANSFER_TYPE", CASE_TRANSFER_TYPE_TRANSFER);
    } catch (error) {
      lgg.error(`${LOG_TAG} Case transfer failed to initialize`, {
        error,
        ...lggData,
      });
      throw error;
    }
  },
  async request(context: ActionContext<CaseTransferState, unknown>, req: any) {
    try {
      const data = await CaseTransferService.request(req.caseTransferCode, req.payload);

      lgg.info(`${LOG_TAG} Case transfer requested`, {
        caseTransferCode: req.caseTransferCode,
      });

      context.commit("SET_CASE_TRANSFER", data);
      context.commit("SET_CASE_TRANSFER_TYPE", CASE_TRANSFER_TYPE_REQUEST);
    } catch (error) {
      lgg.error(`${LOG_TAG} Case transfer failed to request`, { error });
      throw error;
    }
  },
  async accept(context: ActionContext<CaseTransferState, unknown>, caseTransferId: string) {
    const lggData = { caseTransfer: { id: caseTransferId } };

    try {
      const data = await CaseTransferService.accept(caseTransferId);

      lgg.info(`${LOG_TAG} Case transfer accepted`, lggData);

      context.commit("SET_CASE_TRANSFER", data);
    } catch (error) {
      lgg.error(`${LOG_TAG} Case transfer failed to accept`, {
        error,
        ...lggData,
      });
      throw error;
    }
  },
  async decline(context: ActionContext<CaseTransferState, unknown>, caseTransferId: string) {
    const lggData = { caseTransfer: { id: caseTransferId } };

    try {
      const data = await CaseTransferService.decline(caseTransferId);

      lgg.info(`${LOG_TAG} Case transfer declined`, lggData);

      context.commit("SET_CASE_TRANSFER", data);
    } catch (error) {
      lgg.error(`${LOG_TAG} Case transfer failed to decline`, {
        error,
        ...lggData,
      });
      throw error;
    }
  },
  async cancel(context: ActionContext<CaseTransferState, unknown>, caseTransferId: string) {
    const lggData = { caseTransfer: { id: caseTransferId } };

    try {
      const data = await CaseTransferService.cancel(caseTransferId);

      lgg.info(`${LOG_TAG} Case transfer cancelled`, lggData);

      context.commit("SET_CASE_TRANSFER", data);
    } catch (error) {
      lgg.error(`${LOG_TAG} Case transfer failed to cancel`, {
        error,
        ...lggData,
      });
      throw error;
    }
  },
  async requestCancel(context: ActionContext<CaseTransferState, unknown>, caseTransferId: string) {
    const lggData = { caseTransfer: { id: caseTransferId } };

    try {
      const data = await CaseTransferService.requestCancel(caseTransferId);

      lgg.info(`${LOG_TAG} Case transfer request cancelled`, lggData);

      context.commit("SET_CASE_TRANSFER", data);
    } catch (error) {
      lgg.error(`${LOG_TAG} Case transfer request failed to cancel`, {
        error,
        ...lggData,
      });
      throw error;
    }
  },
  async state(context: ActionContext<CaseTransferState, unknown>, caseTransferId: string) {
    try {
      const data = await CaseTransferService.state(caseTransferId);
      context.commit("SET_CASE_TRANSFER", data);
    } catch (error) {
      const lggData = { caseTransfer: { id: caseTransferId } };
      lgg.error(`${LOG_TAG} Case transfer state failed`, {
        error,
        ...lggData,
      });
      throw error;
    }
  },
  async received(context: ActionContext<CaseTransferState, unknown>, caseTransferId: string) {
    try {
      const data = await CaseTransferService.received(caseTransferId);
      context.commit("SET_CASE_TRANSFER", data);
    } catch (error) {
      const lggData = { caseTransfer: { id: caseTransferId } };
      lgg.error(`${LOG_TAG} Case transfer received failed`, {
        error,
        ...lggData,
      });
      throw error;
    }
  },
  async acknowledge(context: ActionContext<CaseTransferState, unknown>, caseTransferId: string) {
    try {
      const data = await CaseTransferService.acknowledge(caseTransferId);
      context.commit("SET_CASE_TRANSFER", data);
    } catch (error) {
      const lggData = { caseTransfer: { id: caseTransferId } };
      lgg.error(`${LOG_TAG} Case transfer acknowledge failed`, {
        error,
        ...lggData,
      });
      throw error;
    }
  },
  async receiveCase(context: ActionContext<CaseTransferState, unknown>, caseId: string) {
    const twiageCase = await CaseService.loadCase(caseId);
    context.commit("SET_CASE_TRANSFER_CASE", twiageCase);
  },
  async loadCase(context: ActionContext<CaseTransferState, unknown>, twiageCase: TwiageCase) {
    await context.dispatch("Case/subscribeToPushes", twiageCase.id, { root: true });
    await context.dispatch("Case/setCase", twiageCase, { root: true });
    await context.dispatch("Case/loadTransferredComplaints", undefined, { root: true });
    context.dispatch("clear");

    router.push({ name: "case" }, () => {});
  },
  clear(context: ActionContext<CaseTransferState, unknown>) {
    context.commit("CLEAR_CASE_TRANSFER");
  },
};

const mutations = {
  SET_CASE_TRANSFER(state: CaseTransferState, caseTransfer: ICaseTransfer) {
    state.caseTransfer = getDefaultCaseTransfer();
    state.caseTransfer = Object.assign(state.caseTransfer, caseTransfer);

    storage.setItem("caseTransfer", JSON.stringify(state.caseTransfer));
  },
  SET_CASE_TRANSFER_TYPE(state: CaseTransferState, caseTransferType: string) {
    state.caseTransferType = caseTransferType;

    storage.setItem("caseTransferType", caseTransferType);
  },
  SET_CASE_TRANSFER_CASE(state: CaseTransferState, caseTransferCase: TwiageCase) {
    state.caseTransferCase = caseTransferCase;

    storage.setItem("caseTransferCase", JSON.stringify(state.caseTransferCase));
  },
  CLEAR_CASE_TRANSFER(state: CaseTransferState) {
    state.caseTransfer = DEFAULT_CASE_TRANSFER;
    state.caseTransferType = null;
    state.caseTransferCase = null;
    storage.removeItem("caseTransfer");
    storage.removeItem("caseTransferType");
    storage.removeItem("caseTransferCase");
  },
};

const store: Module<CaseTransferState, unknown> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export { getters, actions, mutations };

export default store;
